<script setup lang="ts">
import MyBranchAddressDisplayLine from './MyBranchAddressDisplayLine.vue';

const props = withDefaults(
    defineProps<{
        address: Record<string, unknown>;
        index: number;
        isEditing: boolean;
        /** Contact List accordion: summary is in panel title */
        hideSummaryAlert?: boolean;
        canEdit: boolean;
        canDelete: boolean;
        /** MY Register — 카드 요약 + Edit/Delete 텍스트 버튼 */
        registerAddressCardStyle?: boolean;
        summaryTitle?: string;
        showShowAddressButton: boolean;
        showMultiHint: boolean;
        multiHintText?: string;
        showLocationFields: boolean;
        deleteLoading?: boolean;
        countryOptions: Array<{ TOTAL_CODE: string; CODE_NAME: string }>;
        stateOptions: Array<{ TOTAL_CODE: string; CODE_NAME: string }>;
        cityOptions: Array<{ TOTAL_CODE: string; CODE_NAME: string }>;
        postcodeItems: Array<{ TOTAL_CODE: string; CODE_NAME: string }>;
        isLoadingCities?: boolean;
        isLoadingPostcodes?: boolean;
        labels: {
            showAddress: string;
            edit: string;
            cancel: string;
            delete: string;
            alias: string;
            line1: string;
            line2: string;
            line3: string;
            country: string;
            state: string;
            city: string;
            postcode: string;
            pleaseSelect: string;
        };
    }>(),
    {
        hideSummaryAlert: false,
        registerAddressCardStyle: false,
        summaryTitle: '',
        multiHintText: '',
        isLoadingCities: false,
        isLoadingPostcodes: false,
        deleteLoading: false,
    },
);

const emit = defineEmits<{
    'toggle-edit': [];
    remove: [];
    'open-show-address': [];
    'country-focus': [];
    'country-select': [value: string | null | undefined];
    'state-change': [value: string | null | undefined];
    'city-change': [value: string | null | undefined];
    'city-focus': [];
    'postcode-menu': [open: boolean];
    'postcode-change': [];
}>();

function isCascadeSuppressed(): boolean {
    return !!(props.address as { _suppressLocationCascade?: boolean })._suppressLocationCascade;
}

/** State 변경 시 City/Postcode 즉시 비움 — 부모 핸들러 가드와 무관하게 UI 반영 */
function onStateUpdate(value: string | null | undefined) {
    if (!isCascadeSuppressed()) {
        props.address.city = '';
        props.address.post_code = '';
        props.address.postcode = '';
        props.address.postcodeCode = '';
    }
    emit('state-change', value);
}

/** City 변경 시 Postcode 즉시 비움 */
function onCityUpdate(value: string | null | undefined) {
    if (!isCascadeSuppressed()) {
        props.address.post_code = '';
        props.address.postcode = '';
        props.address.postcodeCode = '';
    }
    emit('city-change', value);
}
</script>

<template>
    <div class="contact-residential-branch-row-editor">
        <div
            v-if="!registerAddressCardStyle || isEditing"
            class="d-flex justify-end align-center ga-1 mb-1 flex-wrap"
        >
            <v-btn
                v-show="canEdit && isEditing && showShowAddressButton"
                size="small"
                variant="outlined"
                color="primary"
                @click.stop="emit('open-show-address')"
            >
                <v-icon start size="16">mdi-map-marker-multiple</v-icon>
                {{ labels.showAddress }}
            </v-btn>
            <v-btn
                v-show="canEdit && (!registerAddressCardStyle || isEditing)"
                size="small"
                variant="text"
                color="primary"
                @click="emit('toggle-edit')"
            >
                <v-icon start size="16">{{ isEditing ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
                {{ isEditing ? labels.cancel : labels.edit }}
            </v-btn>
            <v-btn
                v-if="canEdit && canDelete && hideSummaryAlert && !isEditing && !registerAddressCardStyle"
                icon
                variant="outlined"
                size="small"
                color="error"
                density="comfortable"
                class="flex-shrink-0"
                :loading="deleteLoading"
                :aria-label="labels.delete"
                @click.stop="emit('remove')"
            >
                <v-icon size="20">mdi-minus-circle-outline</v-icon>
            </v-btn>
        </div>

        <v-card
            v-if="registerAddressCardStyle && !isEditing"
            variant="outlined"
            rounded="lg"
            class="register-address-card mb-2"
        >
            <div class="register-address-card-body">
                <div class="register-address-card-main">
                    <div v-if="summaryTitle" class="register-address-card-title">{{ summaryTitle }}</div>
                    <div class="register-address-card-text">
                        <MyBranchAddressDisplayLine
                            :address="address"
                            :country-opts="countryOptions"
                            :state-opts="stateOptions"
                            :city-opts="cityOptions"
                            :postcode-opts="postcodeItems"
                        />
                    </div>
                </div>
                <div class="d-flex align-center ga-2 flex-shrink-0">
                    <v-btn
                        v-if="canEdit"
                        size="small"
                        variant="outlined"
                        color="primary"
                        rounded="pill"
                        class="register-address-action-btn"
                        @click="emit('toggle-edit')"
                    >
                        <v-icon start size="16">mdi-pencil</v-icon>
                        {{ labels.edit }}
                    </v-btn>
                    <v-btn
                        v-if="canEdit && canDelete"
                        size="small"
                        variant="outlined"
                        color="error"
                        rounded="pill"
                        class="register-address-action-btn"
                        :loading="deleteLoading"
                        @click.stop="emit('remove')"
                    >
                        {{ labels.delete }}
                    </v-btn>
                </div>
            </div>
        </v-card>

        <v-alert
            v-show="!registerAddressCardStyle && !hideSummaryAlert && !isEditing"
            type="info"
            variant="tonal"
            density="comfortable"
        >
            <div class="d-flex justify-space-between align-center flex-wrap gap-2">
                <div class="d-flex align-center flex-wrap flex-grow-1 ga-2">
                    <MyBranchAddressDisplayLine
                        :address="address"
                        :country-opts="countryOptions"
                        :state-opts="stateOptions"
                        :city-opts="cityOptions"
                        :postcode-opts="postcodeItems"
                    />
                    <v-btn
                        v-if="canEdit && canDelete"
                        icon
                        variant="outlined"
                        size="small"
                        color="error"
                        density="comfortable"
                        class="flex-shrink-0"
                        :loading="deleteLoading"
                        :aria-label="labels.delete"
                        @click.stop="emit('remove')"
                    >
                        <v-icon size="20">mdi-minus-circle-outline</v-icon>
                    </v-btn>
                </div>
                <div v-if="index === 0 && showMultiHint" class="d-flex align-center ga-2 flex-wrap">
                    <v-icon size="18" color="warning" icon="mdi-alert-circle-outline" />
                    <span class="text-caption text-medium-emphasis">{{ multiHintText }}</span>
                </div>
            </div>
        </v-alert>

        <div v-if="index === 0 && showMultiHint && hideSummaryAlert" class="d-flex align-center ga-2 flex-wrap mb-2">
            <v-icon size="18" color="warning" icon="mdi-alert-circle-outline" />
            <span class="text-caption text-medium-emphasis">{{ multiHintText }}</span>
        </div>

        <div v-if="isEditing" class="mb-2">
            <v-row v-if="!registerAddressCardStyle" dense>
                <v-col cols="12">
                    <v-text-field
                        v-model="address.alias"
                        :label="labels.alias"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                        clearable
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12">
                    <v-text-field
                        v-model="address.street01"
                        :label="labels.line1"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12">
                    <v-text-field
                        v-model="address.street02"
                        :label="labels.line2"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12">
                    <v-text-field
                        v-model="address.street03"
                        :label="labels.line3"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="6">
                    <v-autocomplete
                        v-model="address.country"
                        v-model:search="address._countrySearch"
                        :items="countryOptions"
                        :label="labels.country"
                        item-title="CODE_NAME"
                        item-value="TOTAL_CODE"
                        variant="outlined"
                        density="comfortable"
                        :placeholder="labels.pleaseSelect"
                        hide-details
                        clearable
                        :auto-select-first="false"
                        @focus="emit('country-focus')"
                        @update:model-value="emit('country-select', $event)"
                    />
                </v-col>
                <v-col v-if="showLocationFields" cols="12" md="6">
                    <v-autocomplete
                        v-model="address.state"
                        :items="stateOptions"
                        :label="labels.state"
                        item-title="CODE_NAME"
                        item-value="TOTAL_CODE"
                        variant="outlined"
                        density="comfortable"
                        :placeholder="labels.pleaseSelect"
                        hide-details
                        clearable
                        :loading="isLoadingCities"
                        @update:model-value="onStateUpdate"
                    />
                </v-col>
            </v-row>
            <v-row v-if="showLocationFields" dense>
                <v-col cols="12" md="6">
                    <v-autocomplete
                        v-model="address.city"
                        :items="cityOptions"
                        :label="labels.city"
                        item-title="CODE_NAME"
                        item-value="TOTAL_CODE"
                        variant="outlined"
                        density="comfortable"
                        :placeholder="labels.pleaseSelect"
                        hide-details
                        clearable
                        :loading="isLoadingCities"
                        @focus="emit('city-focus')"
                        @click:control="emit('city-focus')"
                        @update:model-value="onCityUpdate"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-autocomplete
                        v-model="address.post_code"
                        :items="postcodeItems"
                        :label="labels.postcode"
                        item-title="CODE_NAME"
                        item-value="TOTAL_CODE"
                        variant="outlined"
                        density="comfortable"
                        :placeholder="labels.pleaseSelect"
                        hide-details
                        clearable
                        :loading="isLoadingPostcodes"
                        @update:menu="emit('postcode-menu', $event)"
                        @update:model-value="emit('postcode-change')"
                    />
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<style scoped>
.register-address-card {
    background: rgb(var(--v-theme-surface)) !important;
    border-color: rgba(var(--v-border-color), var(--v-border-opacity)) !important;
}

.register-address-card-body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 18px 20px;
    flex-wrap: nowrap;
}

.register-address-card-main {
    flex: 1 1 auto;
    min-width: 0;
}

.register-address-card-title {
    font-size: 1rem;
    font-weight: 700;
    color: rgb(var(--v-theme-on-surface));
    line-height: 1.4;
    margin-bottom: 8px;
}

.register-address-card-text {
    font-size: 0.875rem;
    font-weight: 400;
    color: rgba(var(--v-theme-on-surface), 0.68);
    line-height: 1.5;
    word-break: break-word;
}

.register-address-action-btn {
    flex-shrink: 0;
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
    height: 36px !important;
    min-width: 84px;
    padding-inline: 16px !important;
}
</style>
